import { api, useApiMutation, useApiQuery } from "../../api";
import type { User, Uuid } from "../../api";
import { Card, useToast } from "../../components";
import { useSession } from "../../session";
import { AsyncBoundary } from "../../shell";
import styles from "../feuilles/Feuilles.module.css";

export type FeuillesDestinatairesProps = {
  companyId: Uuid;
  /** Tous les membres de l'entreprise, déjà chargés par l'écran d'équipe. */
  membres: User[];
};

/**
 * Qui reçoit et valide les feuilles de temps de l'entreprise.
 *
 * Les administrateurs sont cochés et grisés : destinataires de droit, on ne
 * les décoche pas. Les membres se cochent ; chaque changement est enregistré
 * tout de suite. Si c'est soi-même qu'on ajoute, la session est relue pour
 * que l'entrée « Feuilles de temps » apparaisse sans se déconnecter.
 */
export function FeuillesDestinataires({ companyId, membres }: FeuillesDestinatairesProps) {
  const { notify, notifyError } = useToast();
  const { state, refresh } = useSession();
  const destinataires = useApiQuery((o) => api.getTimesheetRecipients(companyId, o), [companyId]);
  const enregistrement = useApiMutation(api.setTimesheetRecipients);

  const actifs = membres.filter((u) => u.status !== "disabled");
  const moi = state.status === "authenticated" ? state.user.id : null;

  async function basculer(u: User, coche: boolean, extra: User[]) {
    const ids = new Set(extra.map((e) => e.id));
    if (coche) {
      ids.add(u.id);
    } else {
      ids.delete(u.id);
    }
    const maj = await enregistrement.run(companyId, { person_ids: [...ids] });
    if (maj === null) {
      notifyError(enregistrement.error, "Destinataires non enregistrés");
      return;
    }
    destinataires.reload();
    notify({ tone: "success", title: coche ? `${u.email} recevra les feuilles` : `${u.email} ne recevra plus les feuilles` });
    if (u.id === moi) {
      void refresh();
    }
  }

  return (
    <Card
      title="Destinataires des feuilles de temps"
      subtitle="Qui est prévenu quand un consultant soumet sa feuille, et qui peut l'approuver ou la refuser. Les administrateurs le sont toujours."
    >
      <AsyncBoundary state={destinataires} errorTitle="Destinataires illisibles">
        {(donnees) => {
          const cochesExtra = new Set(donnees.extra.map((u) => u.id));
          return (
            <div className={styles.destinataires}>
              {actifs.map((u) => {
                const admin = u.role === "admin";
                return (
                  <label key={u.id} className={`${styles.case} ${admin ? styles.caseGrisee : ""}`}>
                    <input
                      type="checkbox"
                      checked={admin || cochesExtra.has(u.id)}
                      disabled={admin || enregistrement.pending}
                      onChange={(e) => void basculer(u, e.target.checked, donnees.extra)}
                    />
                    <span>{u.email}</span>
                    {admin && <span className={styles.droit}>toujours destinataire</span>}
                    {u.status === "invited" && <span className={styles.droit}>invitation en attente</span>}
                  </label>
                );
              })}
              {actifs.length === 0 && <p className={styles.mention}>Aucun membre pour l'instant.</p>}
              <p className={styles.mention}>
                Un membre ajouté verra l'entrée « Feuilles de temps » à sa prochaine connexion.
              </p>
            </div>
          );
        }}
      </AsyncBoundary>
    </Card>
  );
}
